import {
  APIFetchBadgesV1,
  APIFetchKronos,
  APIFetchV2,
  RequestHeaders,
  serverlessAPIFetch,
  tenant
} from "@certa/network";
import type {
  AccessAttemptsResult,
  AdminSettingsActivityLogFilters,
  AdminSettingsActivityLogsResponse,
  Branding,
  BrandingParams,
  ConstanceConfigs,
  ConstanceConfigsResult,
  CreateIntegrationConfigParams,
  InstanceSettingsBusinessUnit,
  InstanceSettingsRegion,
  InstanceSettingsUserGroup,
  IntegrationConfigResponse,
  IntegrationTokenParams,
  IntegrationTokenResponse,
  IntegrationTokensListResponse,
  TaskLogResult,
  UpdateIntegrationConfigParams,
  UserConfigCreateParams,
  UserConfigResponse,
  UserConfigUpdateParams,
  UsersParams,
  ConstanceConfig,
  UpdateDescIconPayload,
  CreateBadgePayload,
  BadgeResponse,
  UpdateBadgePayload
} from "../types/instanceSettings.types";
import type { HashMap } from "@certa/types";
import {
  accessAttemptsModelCreator,
  adminSettingsActivityLogsModelCreator,
  badgeDefinitionModelCreator,
  badgeDefinitionsModelCreatorPaginated,
  constanceConfigsModelCreator,
  integrationConfigsModelCreator,
  integrationTokenModelCreator,
  integrationTokensListModelCreator,
  taskLogModelCreator,
  userConfigModelCreator,
  userConfigModelCreatorAll,
  userConfigsModelCreatorPaginated
} from "../models/instanceSettings.model";
import type { PaginatedResultsResponse } from "../types/generic.types";
import type {
  Scheduler,
  SchedulerFull
} from "../schemas/instanceSettings/scheduler";

export const getBranding = () => {
  const requestOptions: RequestInit = {
    method: "GET",
    headers: RequestHeaders.GET,
    credentials: "include"
  };

  const url = `admin-settings/customer/`;

  return APIFetchV2<Branding>(url, requestOptions);
};

export const setBranding = (payload: Partial<BrandingParams>) => {
  // Needs krypton.can_access_admin_settings permission
  const requestOptions: RequestInit = {
    method: "POST",
    headers: RequestHeaders.POST,
    credentials: "include"
  };

  if (payload.logo || payload.mini_logo) {
    requestOptions.body = new FormData();
    if (payload.logo) {
      requestOptions.body.append("logo", payload.logo);
    }
    if (payload.mini_logo) {
      requestOptions.body.append("mini_logo", payload.mini_logo);
    }

    if (payload.name) {
      requestOptions.body.append("name", payload.name);
    }
    delete (requestOptions.headers as HashMap)["Content-Type"];
  } else {
    requestOptions.body = JSON.stringify(payload);
  }

  const url = `admin-settings/customer/`;

  return APIFetchV2(url, requestOptions);
};

export const setWfDescIcon = (
  payload: Partial<UpdateDescIconPayload>
): Promise<void> => {
  const { id, description, icon } = payload;
  const requestOptions: RequestInit = {
    method: "PATCH",
    headers: RequestHeaders.POST,
    credentials: "include",
    body: JSON.stringify({
      description: description || "", // required to set description to empty string when its undefined
      icon: icon || "" // required to set icon to empty string when its undefined
    })
  };

  const url = `admin-settings/workflow-kinds/${id}/update_workflow_details/`;
  return APIFetchV2(url, requestOptions);
};

export const getIntegrationTokens = () => {
  const requestOptions: RequestInit = {
    method: "GET",
    headers: RequestHeaders.GET,
    credentials: "include"
  };

  const url = `admin-settings/integration-tokens/?limit=none`;

  return APIFetchV2<IntegrationTokensListResponse[]>(url, requestOptions).then(
    integrationTokensListModelCreator
  );
};

export const getIntegrationToken = (id: number) => {
  const requestOptions: RequestInit = {
    method: "GET",
    headers: RequestHeaders.GET,
    credentials: "include"
  };
  const url = `admin-settings/integration-tokens/${id}`;

  return APIFetchV2<IntegrationTokenResponse>(url, requestOptions).then(
    integrationTokenModelCreator
  );
};

export const createIntegrationToken = (payload: IntegrationTokenParams) => {
  const requestOptions: RequestInit = {
    method: "POST",
    headers: RequestHeaders.POST,
    credentials: "include",
    body: JSON.stringify(payload)
  };

  const url = `admin-settings/integration-tokens/`;
  return APIFetchV2<IntegrationTokenResponse>(url, requestOptions);
};

export const updateIntegrationToken = (
  payload: IntegrationTokenParams & { id: number } // making ID mandatory here
) => {
  const finalPayload: IntegrationTokenParams = { ...payload };
  delete finalPayload.id;
  const requestOptions: RequestInit = {
    method: "PATCH",
    headers: RequestHeaders.POST,
    credentials: "include",
    body: JSON.stringify(finalPayload)
  };

  const url = `admin-settings/integration-tokens/${payload.id}/`;

  return APIFetchV2(url, requestOptions);
};

export const deleteIntegrationToken = (id: number) => {
  const requestOptions: RequestInit = {
    method: "DELETE",
    headers: RequestHeaders.POST,
    credentials: "include"
  };

  const url = `admin-settings/integration-tokens/${id}/`;
  return APIFetchV2(url, requestOptions);
};

export const getIntegrationConfigs = () => {
  const requestOptions: RequestInit = {
    method: "GET",
    headers: RequestHeaders.GET,
    credentials: "include"
  };

  const url = `admin-settings/integration-configs/?limit=none`;

  return APIFetchV2<IntegrationConfigResponse[]>(url, requestOptions).then(
    integrationConfigsModelCreator
  );
};

export const createIntegrationConfig = (
  payload: CreateIntegrationConfigParams
) => {
  const requestOptions: RequestInit = {
    method: "POST",
    headers: RequestHeaders.POST,
    credentials: "include",
    body: JSON.stringify(payload)
  };

  const url = `admin-settings/integration-configs/`;
  return APIFetchV2<IntegrationConfigResponse>(url, requestOptions);
};

export const updateIntegrationConfig = (
  payload: UpdateIntegrationConfigParams
) => {
  const finalPayload: UpdateIntegrationConfigParams = { ...payload };
  delete finalPayload.id; // we don't want ID in "body"
  const requestOptions: RequestInit = {
    method: "PATCH",
    headers: RequestHeaders.POST,
    credentials: "include",
    body: JSON.stringify(finalPayload)
  };

  const url = `admin-settings/integration-configs/${payload.id}/`;

  return APIFetchV2(url, requestOptions);
};

export const getInstanceSettingBusinessUnits = () => {
  const requestOptions: RequestInit = {
    method: "GET",
    headers: RequestHeaders.GET,
    credentials: "include"
  };
  const url = `admin-settings/business-units/?limit=none`;

  return APIFetchV2<InstanceSettingsBusinessUnit[]>(url, requestOptions);
};

export const getInstanceSettingRegions = () => {
  const requestOptions: RequestInit = {
    method: "GET",
    headers: RequestHeaders.GET,
    credentials: "include"
  };
  const url = `admin-settings/regions/?limit=none`;

  return APIFetchV2<InstanceSettingsRegion[]>(url, requestOptions);
};

export const getInstanceSettingUserGroups = (offset = 0) => {
  const requestOptions: RequestInit = {
    method: "GET",
    headers: RequestHeaders.GET,
    credentials: "include"
  };
  const url = `admin-settings/groups/?offset=${offset}&limit=500`;

  return APIFetchV2<PaginatedResultsResponse<InstanceSettingsUserGroup>>(
    url,
    requestOptions
  );
};

export const getInstanceUsers = (params: UsersParams) => {
  const requestOptions: RequestInit = {
    method: "GET",
    headers: RequestHeaders.GET,
    credentials: "include"
  };

  const queryParams: Record<string, string> = {
    limit: params?.limit?.toString() || "20"
  };

  if (params?.offset?.length) {
    queryParams["offset"] = params.offset.toString();
  }
  if (params?.email?.length) {
    queryParams["email__in"] = params.email;
  }
  if (params?.firstName?.length) {
    queryParams["first_name__in"] = params.firstName;
  }
  if (params?.lastName?.length) {
    queryParams["last_name__in"] = params.lastName;
  }
  if (params?.isActive?.length) {
    queryParams["is_active"] = params.isActive.toString();
  }
  if (params?.group?.length) {
    queryParams["group"] = params.group.toString();
  }

  const stringifyQueryParams = new URLSearchParams(queryParams).toString();

  const url = `admin-settings/users/?${stringifyQueryParams}`;

  // If limit is none that means we want all the users at once
  const isNotPaginated = queryParams.limit === "none";

  return isNotPaginated
    ? APIFetchV2<UserConfigResponse[]>(url, requestOptions).then(
        userConfigModelCreatorAll
      )
    : APIFetchV2<PaginatedResultsResponse<UserConfigResponse>>(
        url,
        requestOptions
      ).then(userConfigsModelCreatorPaginated);
};

export const createInstanceUser = (payload: UserConfigCreateParams) => {
  const actualPayload = { ...payload };
  delete actualPayload.id;

  const requestOptions: RequestInit = {
    method: "POST",
    headers: RequestHeaders.POST,
    credentials: "include",
    body: JSON.stringify(actualPayload)
  };
  const url = `admin-settings/users/`;

  return APIFetchV2<UserConfigResponse>(url, requestOptions).then(
    userConfigModelCreator
  );
};

export const updateInstanceUser = (payload: UserConfigUpdateParams) => {
  const actualPayload = { ...payload };
  delete actualPayload.id;

  const requestOptions: RequestInit = {
    method: "PATCH",
    headers: RequestHeaders.POST,
    credentials: "include",
    body: JSON.stringify(actualPayload)
  };
  const url = `admin-settings/users/${payload.id}/`;

  return APIFetchV2<UserConfigResponse>(url, requestOptions).then(
    userConfigModelCreator
  );
};

export const getAdminSettingsActivityLogs = (
  pageParam: number,
  options?: AdminSettingsActivityLogFilters
) => {
  const requestOptions: RequestInit = {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      "X-TENANT": tenant
    },
    mode: "cors",
    credentials: "include"
  };
  const queryParams: Record<string, string> = {
    object_type: options?.objectType || "",
    offset: pageParam.toString(),
    limit: "500"
  };

  if (options?.actions) {
    queryParams["actions"] = options.actions;
  }

  if (options?.actionTypes) {
    queryParams["action_types"] = options.actionTypes;
  }

  if (options?.actorEmail) {
    queryParams["actor_email"] = options.actorEmail;
  }

  if (options?.actorName) {
    queryParams["actor_name"] = options.actorName;
  }

  const queryString = new URLSearchParams(queryParams).toString();
  const url = `admin-settings/activity/?${queryString}`;

  return serverlessAPIFetch<AdminSettingsActivityLogsResponse>(
    url,
    requestOptions
  ).then(adminSettingsActivityLogsModelCreator);
};

export const getConstanceConfigs = (): Promise<ConstanceConfigs> => {
  const url = `admin-settings/constance-configs/`;
  const requestOptions: RequestInit = {
    method: "GET",
    headers: RequestHeaders.GET,
    credentials: "include"
  };
  return APIFetchV2<ConstanceConfigsResult>(url, requestOptions).then(
    constanceConfigsModelCreator
  );
};

export const updateConstanceConfig = (payload: ConstanceConfig) => {
  const url = `admin-settings/constance-configs/${payload?.key}/`;
  const requestOptions: RequestInit = {
    method: "PUT",
    headers: RequestHeaders.PUT,
    credentials: "include",
    body: JSON.stringify(payload)
  };

  return APIFetchV2<ConstanceConfig>(url, requestOptions);
};

export const getAllAccessAttempts = () => {
  const url = `admin-settings/access-attempts/?limit=none`;
  const requestOptions: RequestInit = {
    method: "GET",
    headers: RequestHeaders.GET,
    credentials: "include"
  };
  return APIFetchV2<AccessAttemptsResult[]>(url, requestOptions).then(
    accessAttemptsModelCreator
  );
};

export const deleteAccessAttempts = (payload: number[]): Promise<void> => {
  const url = "admin-settings/access-attempts/bulk-delete/";
  const requestOptions: RequestInit = {
    method: "DELETE",
    headers: RequestHeaders.POST,
    credentials: "include",
    body: JSON.stringify({ delete_ids: payload })
  };
  return APIFetchV2(url, requestOptions);
};

export const getTaskLogs = (payload: {
  statuses: string;
  taskLogType: string;
}) => {
  const url = `admin-settings/task-logs/?limit=none&status__in=${payload?.statuses}&task_type=${payload.taskLogType}`;
  const requestOptions: RequestInit = {
    method: "GET",
    headers: RequestHeaders.GET,
    credentials: "include"
  };
  return APIFetchV2<PaginatedResultsResponse<TaskLogResult>>(
    url,
    requestOptions
  ).then(taskLogModelCreator);
};

export const bulkImportUsers = (payload: File) => {
  const url = "admin-settings/users/bulk-import/";
  const requestOptions: RequestInit = {
    method: "POST",
    headers: RequestHeaders.POST,
    credentials: "include"
  };
  requestOptions.body = new FormData();
  requestOptions.body.append("uploaded_file", payload);

  // Unsetting the header for multipart-formdata
  delete (requestOptions.headers as HashMap)["Content-Type"];

  return APIFetchV2<{ task_tracking_id: number }>(url, requestOptions);
};

// BADGE DEFINITIONS //////////////////////////////////////////////////////////
export const getBadgeDefinitions = (
  offset = 0,
  contentType?: "workflow" | "object",
  typeTag?: string
) => {
  const basePath = contentType ? `badges/${contentType}` : "badges";
  const queryParams = new URLSearchParams({
    offset: offset.toString(),
    limit: "20",
    ...(typeTag && { type_tag: typeTag })
  });

  const url = `${basePath}?${queryParams.toString()}`;

  const requestOptions: RequestInit = {
    method: "GET",
    headers: RequestHeaders.GET,
    credentials: "include"
  };
  return APIFetchBadgesV1<PaginatedResultsResponse<BadgeResponse>>(
    url,
    requestOptions
  ).then(badgeDefinitionsModelCreatorPaginated);
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const getBadgeDefinitionsFilteredByContentType = (
  offset = 0,
  contentType: "workflow" | "object",
  contentTypeTag: string
) => {
  const url = `badges/?offset=${offset}&limit=20&content_type=${contentType}&type_tag=${contentTypeTag}/`;
  const requestOptions: RequestInit = {
    method: "GET",
    headers: RequestHeaders.GET,
    credentials: "include"
  };
  return APIFetchBadgesV1<PaginatedResultsResponse<BadgeResponse>>(
    url,
    requestOptions
  ).then(badgeDefinitionsModelCreatorPaginated);
};

export const createBadgeDefinition = (payload: CreateBadgePayload) => {
  const url = `badges/`;
  const requestOptions: RequestInit = {
    method: "POST",
    headers: RequestHeaders.POST,
    credentials: "include",
    body: JSON.stringify(payload)
  };
  return APIFetchBadgesV1<BadgeResponse>(url, requestOptions).then(
    badgeDefinitionModelCreator
  );
};

export const updateBadgeDefinition = (payload: UpdateBadgePayload) => {
  const url = `badges/${payload.tag}/`;
  const requestOptions: RequestInit = {
    method: "PUT",
    headers: RequestHeaders.PUT,
    credentials: "include",
    body: JSON.stringify(payload)
  };
  return APIFetchBadgesV1<BadgeResponse>(url, requestOptions).then(
    badgeDefinitionModelCreator
  );
};

export const deleteBadgeDefinition = (tag: string) => {
  const url = `badges/${tag}/`;
  const requestOptions: RequestInit = {
    method: "DELETE",
    headers: RequestHeaders.POST,
    credentials: "include"
  };
  return APIFetchBadgesV1<void>(url, requestOptions);
};

// SCHEDULER ///////////////////////////////////////////////////////////////////

export const getAllSchedules = () => {
  const url = `scheduler/?limit=none`;
  const requestOptions: RequestInit = {
    method: "GET",
    headers: RequestHeaders.GET,
    credentials: "include"
  };
  return APIFetchKronos<SchedulerFull[]>(url, requestOptions);
};

export const updateScheduler = (payload: SchedulerFull) => {
  const url = `scheduler/${payload.id}/`;
  const requestOptions: RequestInit = {
    method: "PATCH",
    headers: RequestHeaders.PUT,
    credentials: "include",
    body: JSON.stringify(payload)
  };
  return APIFetchKronos(url, requestOptions);
};

export const createScheduler = (payload: Scheduler) => {
  const url = `scheduler/`;
  const requestOptions: RequestInit = {
    method: "POST",
    headers: RequestHeaders.POST,
    credentials: "include",
    body: JSON.stringify(payload)
  };
  return APIFetchKronos<Scheduler>(url, requestOptions);
};

export const deleteScheduler = (id: number) => {
  const url = `scheduler/${id}/`;
  const requestOptions: RequestInit = {
    method: "DELETE",
    headers: RequestHeaders.POST,
    credentials: "include"
  };
  return APIFetchKronos<void>(url, requestOptions);
};
